import { useCallback, useEffect, useRef } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

const CSS_VAR = "--ai-chat-width";
const STORAGE_KEY = "ai-chat.panelWidth";

export const DEFAULT_PANEL_WIDTH = 380;
export const MIN_PANEL_WIDTH = 320;
export const MAX_PANEL_WIDTH = 1080;

// Keep a readable page column next to the panel, and match the shell's sidebar geometry so the
// content-safe maximum accounts for whichever sidebar state is showing.
const MIN_CONTENT_WIDTH = 360;
const LEFTBAR_WIDTH = 240;
const LEFTBAR_COLLAPSED = 70;

/**
 * Clamp a desired panel width to something usable: never thinner than the chat needs, never so wide
 * the page content is crushed (nor past an absolute cap). Pure so it can be unit-tested without DOM.
 */
export function clampWidth(desired: number, viewportWidth: number, sidebarWidth: number): number {
  const contentSafeMax = viewportWidth - sidebarWidth - MIN_CONTENT_WIDTH;
  const upper = Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, contentSafeMax));
  return Math.min(Math.max(desired, MIN_PANEL_WIDTH), upper);
}

function currentSidebarWidth(): number {
  return document.body.classList.contains("desktop-collapse-sidebar") ? LEFTBAR_COLLAPSED : LEFTBAR_WIDTH;
}

function readWidthVar(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(CSS_VAR);
  return parseFloat(raw) || DEFAULT_PANEL_WIDTH;
}

function writeWidthVar(width: number): void {
  document.documentElement.style.setProperty(CSS_VAR, `${width}px`);
}

// Width persistence is best-effort: browsers with storage blocked throw on any
// localStorage access, and that must never break the panel itself.
function readStoredWidth(): number {
  try {
    return Number(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return 0;
  }
}

function persistWidth(width: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(width));
  } catch {
    // best-effort only
  }
}

/**
 * Right-edge drag-to-resize for the docked chat panel. The width is written straight to the
 * `--ai-chat-width` CSS variable (not React state), so the panel and the content margin — a calc()
 * on the same variable — track the pointer at 60fps without re-rendering. The width is clamped to
 * keep both the chat and the page usable, and persisted to localStorage across sessions.
 *
 * Returns pointer handlers to spread onto the drag handle element.
 */
export function useResizablePanel() {
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);

  // Restore the saved width once, re-clamped to the current viewport.
  useEffect(() => {
    const stored = readStoredWidth();
    if (stored) {
      writeWidthVar(clampWidth(stored, window.innerWidth, currentSidebarWidth()));
    }
  }, []);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    drag.current = { startX: event.clientX, startWidth: readWidthVar() };
    event.currentTarget.setPointerCapture(event.pointerId);
    document.body.classList.add("ai-chat-resizing");
  }, []);

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) {
      return;
    }
    const desired = drag.current.startWidth + (event.clientX - drag.current.startX);
    writeWidthVar(clampWidth(desired, window.innerWidth, currentSidebarWidth()));
  }, []);

  const endDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) {
      return;
    }
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    document.body.classList.remove("ai-chat-resizing");
    persistWidth(Math.round(readWidthVar()));
  }, []);

  return { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag };
}
